import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../Admin/AdminDashBoard/admindashboard.css";
import RoleNavigation from "./RoleNavigation";
import useRoleDashboard from "./useRoleDashboard";
import { roleConfigs, dashboardCards } from "./roleDashboardConfig";

const colors = [
  "#17676a",
  "#61aaa0",
  "#b3cec0",
  "#dcb97c",
  "#8595b4",
  "#d7e1df",
];
const brands = [
  ["98 Acres", "98-acres.jpeg"],
  ["Ravana Pool Club", "ravana.jpeg"],
  ["Flying Ravana", "flying-ravana.jpeg"],
  ["Le Maas Tota", "villa.jpeg"],
  ["Tea Factory", "tea-factory.jpeg"],
  ["Walaa kulu", "walaa.jpeg"],
  ["Halpe Tea", "halpe.jpeg"],
  ["Ella Swing", "ella-swing.jpeg"],
  ["Amber Swing", "amber-swing.jpeg"],
  ["Ceylon Tea", "ceylon-tea.jpeg"],
  ["Halpe Tea Tour", "halpe-tour.jpeg"],
];
const groupBy = (items, key) =>
  Object.entries(
    items.reduce((groups, item) => {
      const label = item[key] || "Unassigned";
      groups[label] = (groups[label] || 0) + 1;
      return groups;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
export default function RoleDashboard({ role }) {
  const config = roleConfigs[role];
  const { data, loading, profile, error, retry } = useRoleDashboard(role);
  const { assets, users, pending } = data;
  const [company, setCompany] = useState("");
  const scopeLabel = config.scope === "group"
    ? "A clear view of group assets, verification and disposal."
    : [profile?.companyName, config.scope === "department" ? profile?.department : null].filter(Boolean).join(" / ") || "Your workspace";
  const filtered = (assets || []).filter(asset =>
    !company || (asset[config.distribution] || "Unassigned") === company
  );
  const categories = groupBy(filtered, "category");
  const chartGroups = categories.length > 6
    ? [...categories.slice(0, 5), ["Other categories", categories.slice(5).reduce((sum, row) => sum + row[1], 0)]]
    : categories;
  let offset = 0;
  const gradient = chartGroups.map(([, count], index) => {
    const start = offset;
    offset += count / filtered.length * 100;
    return colors[index] + " " + start + "% " + offset + "%";
  }).join(", ");
  const companies = groupBy(assets || [], config.distribution);
  const display = value => loading ? "..." : value == null ? "-" : value.toLocaleString();
  const cards = dashboardCards(role, data, profile);
  const teamRoles = role === "company"
    ? [["CompanyAdmin", "Company administrators"], ["DepartmentAdmin", "Department administrators"], ["OtherUser", "Other users"]]
    : [["DepartmentAdmin", "Department administrators"], ["OtherUser", "Other users"]];
  return (
    <div className="vl-dashboard-page">
      <RoleNavigation role={role} profile={profile} />
      <main className="vl-dashboard">
        <div className="vl-breadcrumb">
          Workspace <span>/</span> <strong>Overview</strong>
        </div>
        <div className="vl-page-heading">
          <div>
            <div className="vl-eyebrow">{config.title.toUpperCase()}</div>
            <h1>
              Asset overview<span>.</span>
            </h1>
            <p>{scopeLabel}</p>
          </div>
          <Link className="vl-primary" to={config.register}>
            <i className="fas fa-plus" /> Register asset
          </Link>
        </div>
        
        {!loading && error && (
          <div className="vl-error" role="alert">
            {error}
            <button onClick={retry}>Try again</button>
          </div>
        )}
        <section
          className="vl-stats" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(210px, 100%), 1fr))" }}
          aria-label={config.title + " summary"}
          aria-busy={loading}
        >
          {cards.map(([label, count, icon, to, note, tone]) => (
            <Link className={"vl-stat " + tone} to={to} key={label}>
              <div className="vl-stat-top">
                <span>{label}</span>
                <i className={"fas fa-" + icon} />
              </div>
              <strong>{display(count)}</strong>
              <div className="vl-stat-bottom">
                <span>{note}</span>
                <i className="fas fa-arrow-up-right-from-square" />
              </div>
            </Link>
          ))}
        </section>
        <div className="vl-chart-grid">
          <section className="vl-panel">
            <div className="vl-panel-heading">
              <div>
                <h2>Asset composition</h2>
                <p>Your inventory, by category</p>
              </div>
              <label className="vl-select">
                <span className="visually-hidden">Filter chart by {config.distributionLabel}</span>
                <select value={company} onChange={event => setCompany(event.target.value)}>
                  <option value="">All {config.distributionLabel}</option>
                  {companies.map(([name]) => <option key={name} value={name}>{name}</option>)}
                </select>
              </label>
            </div>
            {loading || !assets || !filtered.length ? (
              <div className="vl-empty">
                {loading
                  ? "Loading asset composition..."
                  : !assets
                    ? "Asset data is unavailable."
                    : "No registered assets to display."}
              </div>
            ) : (
              <div className="vl-composition">
                <div
                  className="vl-donut"
                  role="img"
                  aria-label={chartGroups
                    .map(([name, count]) => name + ": " + count)
                    .join(", ")}
                  style={{ background: "conic-gradient(" + gradient + ")" }}
                >
                  <div>
                    <strong>{filtered.length.toLocaleString()}</strong>
                    <span>Total assets</span>
                  </div>
                </div>
                <ul className="vl-legend">
                  {chartGroups.map(([name, count], i) => (
                    <li key={name}>
                      <span
                        className="vl-dot"
                        style={{ background: colors[i] }}
                      />
                      <span>{name}</span>
                      <strong>{count.toLocaleString()}</strong>
                      <small>
                        {Math.round((count / filtered.length) * 100)}%
                      </small>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="vl-panel-foot">
              <span>Based on registered assets</span>
              <Link to={config.inventory}>
                View inventory <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </section>
          <section className="vl-panel">
            <div className="vl-panel-heading">
              <div>
                <h2>Assets by {config.distributionLabel}</h2>
                <p>Where your assets belong</p>
              </div>
              <span className="vl-count">
                {display(assets ? companies.length : null)} {config.distributionLabel}
              </span>
            </div>
            {loading || !assets || !companies.length ? (
              <div className="vl-empty">
                {loading
                  ? "Loading distribution..."
                  : !assets
                    ? "Asset distribution is unavailable."
                    : "Asset distribution will appear as assets are added."}
              </div>
            ) : (
              <div className="vl-bars">
                {companies.slice(0, 5).map(([name, count], i) => (
                  <div className="vl-bar-row" key={name}>
                    <div>
                      <span>{name}</span>
                      <strong>{count.toLocaleString()}</strong>
                    </div>
                    <div className="vl-bar-track">
                      <span
                        style={{
                          width: (count / companies[0][1]) * 100 + "%",
                          background: colors[i],
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="vl-panel-foot">
              <span>Top 5 {config.distributionLabel} by asset count</span>
              <Link to={config.inventory}>
                View inventory <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </section>
        </div>
        <section className="vl-panel vl-workflow">
          <div className="vl-panel-heading">
            <div>
              <h2>Keep things moving</h2>
              <p>Your asset workflow, from registration to management</p>
            </div>
            <span className="vl-subtle">OPERATIONS</span>
          </div>
          <div className="vl-flow" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(210px, 100%), 1fr))" }}>
            {[
              [
                "01",
                "Register",
                "Add an asset to your workspace",
                "circle-plus",
                config.register,
              ],
              [
                "02",
                "Review",
                display(pending?.length) + " registrations pending",
                "clipboard-check",
                config.pending,
              ],
              [
                "03",
                "Manage",
                display(assets?.length) + " assets in your inventory",
                "layer-group",
                config.inventory,
              ],
             
            ].map(([n, title, description, icon, to]) => (
              <Link to={to} key={n}>
                <span className="vl-flow-icon">
                  <i className={"fas fa-" + icon} />
                </span>
                <div>
                  <small>STEP {n}</small>
                  <strong>{title}</strong>
                  <p>{description}</p>
                </div>
                <i className="fas fa-chevron-right" />
              </Link>
            ))}
          </div>
        </section>
        <div className="vl-bottom-grid" style={!config.users ? { gridTemplateColumns: "1fr" } : undefined}>
          <section className="vl-panel">
            <div className="vl-panel-heading">
              <div>
                <h2>One group. Many experiences.</h2>
                <p>Our family of Vella brands</p>
              </div>
            </div>
            <div className="vl-brand-grid">
              {brands.map(([name, file]) => (
                <div key={name} title={name}>
                  <img src={"/brands/" + file} alt={name} loading="lazy" />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </section>
          {config.users && <section className="vl-panel">
            <div className="vl-panel-heading">
              <div>
                <h2>Your team</h2>
                <p>People in your workspace</p>
              </div>
              <Link to={config.users} aria-label="View users">
                <i className="fas fa-arrow-up-right-from-square" />
              </Link>
            </div>
            <div className="vl-team">
              {teamRoles.map(([role, label]) => (
                <div key={role}>
                  <span>{label}</span>
                  <strong>
                    {display(
                      users
                        ? users.filter((user) => user.selectedOption === role)
                            .length
                        : null,
                    )}
                  </strong>
                </div>
              ))}
            </div>
          </section>}
        </div>
        <footer className="vl-footer">
          <span>
            VELLA <span> / </span> Asset Management
          </span>
          <span>Connected companies. Confident decisions.</span>
        </footer>
      </main>
    </div>
  );
}
